/**
 * Cliente HTTP minimalista. En prod la SPA y la API comparten dominio
 * (panel.axorial.mx), así que la cookie CF_Authorization viaja sola.
 * En dev, vite proxea /api/* al worker en :8787.
 */

export class ApiError extends Error {
  constructor(
    public status: number,
    public payload: unknown,
  ) {
    super(`API ${status}`);
  }
}

export class SessionExpiredError extends Error {
  constructor() {
    super("Tu sesión expiró. Recarga la página para volver a entrar.");
  }
}

/** Evento global que dispara el overlay de sesión expirada (AppShell). */
export const SESSION_EXPIRED_EVENT = "axorial:session-expired";

/**
 * Cuando la sesión de Cloudflare Access expira (24h), el edge responde
 * 302 al login en axorial.cloudflareaccess.com; fetch lo sigue cross-origin
 * sin CORS y truena con TypeError "Failed to fetch" — indistinguible de
 * una caída de red. Este probe lo desambigua: con redirect:"manual" un
 * redirect de Access se ve como opaqueredirect sin lanzar.
 */
async function isSessionExpired(): Promise<boolean> {
  try {
    const res = await fetch("/api/health", { redirect: "manual", credentials: "include" });
    return res.type === "opaqueredirect" || res.status === 0;
  } catch {
    return false; // ni el probe salió → problema de red real, no Access
  }
}

async function request<T>(method: string, path: string, body?: unknown): Promise<T> {
  const init: RequestInit = {
    method,
    credentials: "include",
    headers: body instanceof FormData ? {} : { "Content-Type": "application/json" },
    body: body instanceof FormData ? body : body ? JSON.stringify(body) : undefined,
  };
  let res: Response;
  try {
    res = await fetch(`/api${path}`, init);
  } catch (err) {
    if (err instanceof TypeError && (await isSessionExpired())) {
      window.dispatchEvent(new Event(SESSION_EXPIRED_EVENT));
      throw new SessionExpiredError();
    }
    throw err;
  }
  const isJson = res.headers.get("content-type")?.includes("application/json");
  const payload = isJson ? await res.json() : await res.text();
  if (!res.ok) throw new ApiError(res.status, payload);
  return payload as T;
}

export const api = {
  get: <T>(path: string) => request<T>("GET", path),
  post: <T>(path: string, body?: unknown) => request<T>("POST", path, body),
  put: <T>(path: string, body?: unknown) => request<T>("PUT", path, body),
  patch: <T>(path: string, body?: unknown) => request<T>("PATCH", path, body),
  del: <T>(path: string) => request<T>("DELETE", path),
};
